import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  DIALOG_FIELD_AREA_CLASS,
  DIALOG_FIELD_CLASS,
  DialogField,
} from '@/components/ui/dialog-form'
import { DialogPattern } from '@/components/ui/dialog-pattern'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { meetingSummaryTemplatesApi } from '@/lib/api/meeting-summary-templates.api'
import { createLogger } from '@/lib/logger'
import { PlusIcon } from '@phosphor-icons/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { CUSTOM_TEMPLATE_PREFIX } from '@scrambl/shared'

const logger = createLogger('components/meetings/SummaryTemplateSelect')

export const SUMMARY_TEMPLATES_QUERY_KEY = [
  'meeting-summary-templates',
] as const

// SelectItem values must never be '' — 'none' means the general summary
const GENERAL_VALUE = 'none'

interface SummaryTemplateSelectProps {
  /** Template ref (builtin key | 'custom:<uuid>') or null for general */
  value: string | null
  onChange: (templateRef: string | null) => void
  disabled?: boolean
}

/**
 * "Summary style" picker for meeting recordings: built-in meeting types
 * (sales call, standup, 1:1, interview) plus the org's custom templates,
 * with inline creation of new custom templates.
 */
export function SummaryTemplateSelect({
  value,
  onChange,
  disabled,
}: SummaryTemplateSelectProps) {
  const queryClient = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const [draftName, setDraftName] = useState('')
  const [draftInstructions, setDraftInstructions] = useState('')

  const { data } = useQuery({
    queryKey: SUMMARY_TEMPLATES_QUERY_KEY,
    queryFn: () => meetingSummaryTemplatesApi.list(),
    staleTime: 5 * 60 * 1000,
  })

  const createMutation = useMutation({
    mutationFn: () =>
      meetingSummaryTemplatesApi.create({
        name: draftName.trim(),
        instructions: draftInstructions.trim(),
      }),
    onSuccess: template => {
      void queryClient.invalidateQueries({
        queryKey: SUMMARY_TEMPLATES_QUERY_KEY,
      })
      onChange(`${CUSTOM_TEMPLATE_PREFIX}${template.id}`)
      setCreateOpen(false)
      setDraftName('')
      setDraftInstructions('')
      toast.success(`Template "${template.name}" created`)
    },
    onError: error => {
      logger.error('Failed to create template:', error)
      toast.error('Failed to create template')
    },
  })

  return (
    <>
      <div className="flex items-center gap-1.5">
        <Select
          value={value ?? GENERAL_VALUE}
          onValueChange={next => onChange(next === GENERAL_VALUE ? null : next)}
          disabled={disabled}
        >
          <SelectTrigger className="h-8 flex-1 text-sm">
            <SelectValue placeholder="Summary style" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={GENERAL_VALUE}>General summary</SelectItem>
            <SelectGroup>
              <SelectLabel>Meeting types</SelectLabel>
              {(data?.builtin ?? []).map(template => (
                <SelectItem key={template.key} value={template.key}>
                  {template.name}
                </SelectItem>
              ))}
            </SelectGroup>
            {(data?.custom.length ?? 0) > 0 && (
              <>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel>Your organization</SelectLabel>
                  {data?.custom.map(template => (
                    <SelectItem
                      key={template.id}
                      value={`${CUSTOM_TEMPLATE_PREFIX}${template.id}`}
                    >
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </>
            )}
          </SelectContent>
        </Select>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          disabled={disabled}
          onClick={() => setCreateOpen(true)}
          title="New custom template"
        >
          <PlusIcon className="h-3.5 w-3.5" />
        </Button>
      </div>

      <DialogPattern
        open={createOpen}
        onOpenChange={setCreateOpen}
        title="New summary template"
        description="Tell the AI how to structure notes for this meeting type. The template is shared with your organization."
        contentClassName="space-y-4"
        footer={
          <>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setCreateOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              className="min-w-[100px]"
              disabled={
                !draftName.trim() ||
                !draftInstructions.trim() ||
                createMutation.isPending
              }
              onClick={() => createMutation.mutate()}
            >
              Create template
            </Button>
          </>
        }
      >
        <DialogField label="Name" htmlFor="template-name" required>
          <Input
            id="template-name"
            value={draftName}
            onChange={event => setDraftName(event.target.value)}
            placeholder="e.g. Client kickoff"
            maxLength={100}
            className={DIALOG_FIELD_CLASS}
          />
        </DialogField>
        <DialogField
          label="Instructions"
          htmlFor="template-instructions"
          required
        >
          <Textarea
            id="template-instructions"
            value={draftInstructions}
            onChange={event => setDraftInstructions(event.target.value)}
            placeholder={
              'e.g. Structure the summary as: Goals, Scope agreed, Risks raised, Owners & dates. Always capture budget numbers verbatim.'
            }
            rows={5}
            maxLength={4000}
            className={DIALOG_FIELD_AREA_CLASS}
          />
        </DialogField>
      </DialogPattern>
    </>
  )
}
